import { API_BASE } from "../config";
import {
  apiGet,
  apiPost,
  authHeaders,
  extractErrorMessage,
  readJsonSafe,
} from "./client";
import {
  clearLastPosition,
  getStoredUser,
  getStudentContentAccessState,
  getStudentState,
  setStoredUser,
  shouldRememberSession,
} from "./session";
import { changeMyPassword } from "./auth";

export async function fetchStudentProfile() {
  const json = await apiGet("/student/profile");
  setStoredUser(json, shouldRememberSession());
  return json;
}

export async function updateStudentProfile(data) {
  const json = await apiPost("/student/profile/update", data || {});
  setStoredUser(json, shouldRememberSession());
  return json;
}

export async function updateStudentPassword({ oldPassword, newPassword }) {
  return changeMyPassword({ oldPassword, newPassword });
}

function normalizeCourseTask(task = {}) {
  return {
    ...task,
    title: String(task?.title || "").trim(),
  };
}

function normalizeCourseDay(day = {}) {
  return {
    ...day,
    title: String(day?.title || "").trim(),
    tasks: Array.isArray(day?.tasks) ? day.tasks.map(normalizeCourseTask) : [],
  };
}

function normalizeCoursePath(path = {}) {
  return {
    ...path,
    title: String(path?.title || path?.name || "").trim(),
    days: Array.isArray(path?.days) ? path.days.map(normalizeCourseDay) : [],
  };
}

function normalizeCoursePayload(payload) {
  if (!payload || typeof payload !== "object") return payload;
  return {
    ...payload,
    paths: Array.isArray(payload?.paths) ? payload.paths.map(normalizeCoursePath) : [],
  };
}

export async function fetchCourse() {
  const json = await apiGet("/course");

  if (json?.access) {
    const user = getStoredUser() || {};
    setStoredUser(
      {
        ...user,
        subscription_status: json.access.status,
        subscription_until: json.access.expiresAt,
      },
      shouldRememberSession()
    );
  }

  return normalizeCoursePayload(json);
}

export async function fetchResumeLearningOverview() {
  const json = await apiGet("/course/resume-overview");
  const user = getStoredUser() || {};
  const access = json?.access || {};

  setStoredUser(
    {
      ...user,
      account_status: access.account_status || user.account_status,
      subscription_status: access.subscription_status || user.subscription_status,
      subscription_until: access.subscription_until || user.subscription_until,
    },
    shouldRememberSession()
  );

  return json;
}

function isTaskCompleted(task) {
  return task?.is_completed === true || task?.is_completed === 1;
}

function sortByOrderIndexThenId(items = []) {
  return [...items].sort((a, b) => {
    const aOrder = Number.isFinite(Number(a?.order_index))
      ? Number(a.order_index)
      : Number.MAX_SAFE_INTEGER;
    const bOrder = Number.isFinite(Number(b?.order_index))
      ? Number(b.order_index)
      : Number.MAX_SAFE_INTEGER;
    if (aOrder !== bOrder) return aOrder - bOrder;

    const aId = Number.isFinite(Number(a?.id))
      ? Number(a.id)
      : Number.MAX_SAFE_INTEGER;
    const bId = Number.isFinite(Number(b?.id))
      ? Number(b.id)
      : Number.MAX_SAFE_INTEGER;
    return aId - bId;
  });
}

function sortDaysForResume(days = []) {
  return [...days].sort((a, b) => {
    const aDay = Number.isFinite(Number(a?.day_number))
      ? Number(a.day_number)
      : Number.MAX_SAFE_INTEGER;
    const bDay = Number.isFinite(Number(b?.day_number))
      ? Number(b.day_number)
      : Number.MAX_SAFE_INTEGER;
    if (aDay !== bDay) return aDay - bDay;

    const aOrder = Number.isFinite(Number(a?.order_index))
      ? Number(a.order_index)
      : Number.MAX_SAFE_INTEGER;
    const bOrder = Number.isFinite(Number(b?.order_index))
      ? Number(b.order_index)
      : Number.MAX_SAFE_INTEGER;
    if (aOrder !== bOrder) return aOrder - bOrder;

    const aId = Number.isFinite(Number(a?.id))
      ? Number(a.id)
      : Number.MAX_SAFE_INTEGER;
    const bId = Number.isFinite(Number(b?.id))
      ? Number(b.id)
      : Number.MAX_SAFE_INTEGER;
    return aId - bId;
  });
}

function sortPathsForResume(paths = []) {
  return [...paths].sort((a, b) => {
    const aOrder = Number.isFinite(Number(a?.order_index))
      ? Number(a.order_index)
      : Number.MAX_SAFE_INTEGER;
    const bOrder = Number.isFinite(Number(b?.order_index))
      ? Number(b.order_index)
      : Number.MAX_SAFE_INTEGER;
    if (aOrder !== bOrder) return aOrder - bOrder;

    const aId = Number.isFinite(Number(a?.id))
      ? Number(a.id)
      : Number.MAX_SAFE_INTEGER;
    const bId = Number.isFinite(Number(b?.id))
      ? Number(b.id)
      : Number.MAX_SAFE_INTEGER;
    return aId - bId;
  });
}

export function getCourseResumeTarget(courseData) {
  const paths = Array.isArray(courseData?.paths) ? courseData.paths : [];

  for (const path of sortPathsForResume(paths)) {
    const days = Array.isArray(path?.days) ? path.days : [];

    for (const day of sortDaysForResume(days)) {
      const tasks = Array.isArray(day?.tasks) ? day.tasks : [];
      const sortedTasks = sortByOrderIndexThenId(tasks);
      const firstIncompleteTask = sortedTasks.find((task) => !isTaskCompleted(task));

      if (firstIncompleteTask) {
        return {
          found: true,
          isFinished: false,
          pathId: path?.id ?? null,
          pathTitle: String(path?.title || path?.name || "").trim(),
          pathOrderIndex: path?.order_index ?? null,
          dayId: day?.id ?? null,
          dayTitle: String(day?.title || "").trim(),
          dayNumber: day?.day_number ?? null,
          taskId: firstIncompleteTask?.id ?? null,
          taskTitle: String(firstIncompleteTask?.title || "").trim(),
        };
      }
    }
  }

  return {
    found: false,
    isFinished: true,
    pathId: null,
    pathTitle: "",
    pathOrderIndex: null,
    dayId: null,
    dayTitle: "",
    dayNumber: null,
    taskId: null,
    taskTitle: "",
  };
}

export async function fetchTaskById(taskId) {
  const id = Number(taskId);
  if (!Number.isFinite(id) || id <= 0) return null;

  const json = await apiGet(`/tasks/${id}`);
  return json.task || json;
}

export async function fetchQuizAttemptStatus(taskId) {
  const id = Number(taskId);
  if (!Number.isFinite(id) || id <= 0) {
    throw new Error("task id invalid");
  }

  return apiGet(`/quiz-attempts/${id}`);
}

export async function markTaskCompleted(taskId) {
  try {
    const headers = await authHeaders();
    const res = await fetch(`${API_BASE}/course/tasks/${taskId}/complete`, {
      method: "POST",
      headers,
    });

    const json = await readJsonSafe(res);

    if (res.status === 403) {
      if (json?.account_status === "suspended") {
        return { error: "ACCOUNT_SUSPENDED" };
      }
      return { error: "SUBSCRIPTION_EXPIRED" };
    }

    if (res.status === 401) return { error: "UNAUTHORIZED" };
    if (!res.ok) {
      return {
        error: extractErrorMessage(json, "تعذر إكمال المهمة"),
      };
    }

    return json;
  } catch {
    return null;
  }
}

export async function recordTaskLearningActivity(taskId) {
  const id = Number(taskId);
  if (!Number.isFinite(id) || id <= 0) return { ok: false };
  return apiPost(`/course/tasks/${id}/activity`);
}

export async function restartCourse() {
  const json = await apiPost("/course/restart");
  clearLastPosition();
  return json;
}

export async function getTrainingAssignments() {
  const json = await apiGet("/quiz-attempts/training");
  return json.trainings || [];
}

export async function fetchTrainingContent(trainingId) {
  const id = Number(trainingId);
  if (!Number.isFinite(id) || id <= 0) {
    throw new Error("training id invalid");
  }

  return apiGet(`/quiz-attempts/training/${id}/content`);
}

export async function submitTrainingAnswers(trainingId, payload = {}) {
  const id = Number(trainingId);
  if (!Number.isFinite(id) || id <= 0) {
    throw new Error("training id invalid");
  }

  return apiPost(`/quiz-attempts/training/${id}/answers`, payload);
}

export async function submitQuizAttempt(taskId, payload = {}) {
  const id = Number(taskId);
  if (!Number.isFinite(id) || id <= 0) {
    throw new Error("task id invalid");
  }

  return apiPost(`/quiz-attempts/${id}`, payload);
}

export async function markTrainingDone(trainingId) {
  const id = Number(trainingId);
  if (!Number.isFinite(id) || id <= 0) return { ok: false };
  return apiPost(`/quiz-attempts/training/${id}/done`);
}

export async function getWeaknessTop(limit = 3) {
  const n = Number(limit);
  const resolvedLimit = Number.isFinite(n) ? Math.max(1, Math.min(10, n)) : 3;
  const json = await apiGet(`/quiz-attempts/weakness/top?limit=${resolvedLimit}`);
  return json.top || [];
}

export { getStudentContentAccessState, getStudentState };
